/**
 * Our own projection, merged onto the board as a fourth opinion.
 *
 * Rankings use **usage VORP** at a flat games baseline (14.5) — the repeatable
 * layer. Availability is attributed separately and down-weights confidence so
 * low-confidence takes (mostly injury-driven) do not surface as loud edges.
 */

import { scoreProjection } from './espnProjections.js';
import { boardRowId, canonicalTeam } from './playerMatch.js';

const MODEL_JSON = '/model-values.json';

/** Flat games baseline for usage-based ranking — matches `model/ffmodel/attribution.py`. */
export const FLAT_GAMES = 14.5;

/** Below this, vs-ADP edge is hidden — disagreement is mostly noise. */
export const MIN_EDGE_CONFIDENCE = 0.35;

/** Prior volume below this is too thin to trust a model-vs-ADP disagreement. */
export const THIN_SAMPLE_VOLUME = 80;

/** Flags that fully suppress edge — information gaps, not signal. */
export const EDGE_SUPPRESSING_FLAGS = new Set([
  'TRADED',
  'THIN',
  'BELOW_REPLACEMENT',
  'TE_CLUSTER',
  'BACKUP_QB',
]);

export const FLAG_LABELS = {
  TRADED: 'Traded — projection still uses prior-team stats',
  MOVED: 'Changed teams — projection re-scaled to new offence',
  THIN: 'Thin sample — not enough prior volume',
  BELOW_REPLACEMENT: 'Below replacement — ADP prices a starter',
  TE_CLUSTER: 'Mid-tier TE cluster — model cannot separate similar usage',
  BACKUP_QB: 'Backup QB — thin prior volume, not a starter projection',
  AVAILABILITY: 'Availability-driven — low repeatability',
};

export const fetchModelValues = async () => {
  const response = await fetch(MODEL_JSON);
  if (!response.ok) {
    throw new Error(`Could not load model values (${response.status}).`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload?.players) || !payload.players.length) {
    throw new Error('Model values were empty.');
  }
  return payload;
};

let modelCache = null;

export const getModelValuesCached = async () => {
  if (modelCache) return modelCache;
  modelCache = await fetchModelValues();
  return modelCache;
};

/**
 * Human-readable breakdown of why the model ranks a player where it does.
 */
export const formatAttributionTooltip = (row) => {
  const attr = row?.attribution;
  if (!attr) return null;

  const fmt = (n) => `${n >= 0 ? '+' : ''}${Math.round(n)}`;
  const lines = [
    `Usage (high confidence): ${Math.round(attr.usage)} pts`,
    `Efficiency: ${fmt(attr.efficiency)} pts`,
    `Availability (low confidence): ${fmt(attr.availability)} pts`,
    `Primary driver: ${attr.driver}`,
  ];
  if (row.confidence != null) {
    lines.push(`Edge confidence: ${Math.round(row.confidence * 100)}%`);
  }
  const flags = row.flags || [];
  if (flags.length) {
    lines.push(
      `Quality flags: ${flags.map((f) => FLAG_LABELS[f] || f).join('; ')}`,
    );
  }
  return lines.join('\n');
};

export const suppressEdge = (flags) =>
  (flags || []).some((f) => EDGE_SUPPRESSING_FLAGS.has(f));

/** Polymarket / Vegas season prop keys → projection stat keys. */
const PROP_TO_STAT = {
  passing_yards: 'passYd',
  passing_tds: 'passTd',
  rushing_yards: 'rushYd',
  rushing_tds: 'rushTd',
  receiving_yards: 'recYd',
  receiving_tds: 'recTd',
  receptions: 'rec',
};

const PROP_LABELS = {
  passing_yards: 'Pass yds',
  passing_tds: 'Pass TD',
  rushing_yards: 'Rush yds',
  rushing_tds: 'Rush TD',
  receiving_yards: 'Rec yds',
  receiving_tds: 'Rec TD',
  receptions: 'Rec',
};

/**
 * Half-PPR fantasy points implied by preseason prop ladders (Polymarket etc.).
 * Re-scored under the connected league's settings when available.
 */
export const scoreMarketProps = (marketProps, scoringSettings = {}, position) => {
  if (!marketProps || typeof marketProps !== 'object') return null;

  const stats = { fumLost: 0 };
  for (const [propKey, statKey] of Object.entries(PROP_TO_STAT)) {
    const entry = marketProps[propKey];
    if (entry?.implied != null) stats[statKey] = entry.implied;
  }

  if (!stats.rec && stats.recYd && ['WR', 'TE', 'RB'].includes(position)) {
    stats.rec = stats.recYd / 11;
  }
  if (position === 'QB' && stats.passYd && !stats.passInt) {
    stats.passInt = 11;
  }

  return scoreProjection(stats, scoringSettings);
};

/** Tooltip listing each priced prop ladder. */
export const formatMarketPropsTooltip = (row) => {
  const props = row?.marketProps;
  if (!props || typeof props !== 'object') return null;

  const lines = Object.entries(props).map(([key, val]) => {
    const label = PROP_LABELS[key] || key;
    const implied = val?.implied != null ? Math.round(val.implied) : '?';
    const line = val?.line != null ? ` (line ${val.line})` : '';
    return `${label}: ${implied}${line}`;
  });

  if (row.marketPts != null && row.proj != null) {
    const diff = Math.round(row.marketPts - row.proj);
    lines.push(
      `Market total: ${Math.round(row.marketPts)} pts`,
      `Our projection: ${Math.round(row.proj)} pts`,
      `Gap: ${diff >= 0 ? '+' : ''}${diff} pts`,
    );
  }
  if (row.ecrRank != null) {
    lines.push(`ECR: ${row.ecrRank}`);
  }
  if (row.marketAdjusted) {
    lines.push('Our projection is blended toward these market lines.');
  }
  lines.push('Source: Polymarket season prop ladders');
  return lines.join('\n');
};

/**
 * Confidence-scaled edge vs ADP. Returns null when confidence or quality gates fail.
 */
export const scaledEdge = (adpRank, boardRank, confidence, flags = []) => {
  if (adpRank == null || boardRank == null) return null;
  const conf = confidence ?? 0;
  if (conf < MIN_EDGE_CONFIDENCE || suppressEdge(flags)) return null;
  return Math.round((adpRank - boardRank) * conf);
};

export const buildBoardFromModelValues = (payload, scoringSettings) => {
  const players = payload?.players || [];

  return players
    .map((p) => {
      const proj = p.stats ? scoreProjection(p.stats, scoringSettings) : null;
      const healthy = p.healthyGames || 17;
      const flat = p.flatGames ?? FLAT_GAMES;
      const expected =
        proj != null && p.games != null
          ? Math.round((proj * p.games) / healthy * 10) / 10
          : null;
      const usageExpected =
        proj != null ? Math.round((proj * flat) / healthy * 10) / 10 : null;
      const marketPts = scoreMarketProps(p.marketProps, scoringSettings, p.position);
      const marketEdge =
        marketPts != null && proj != null ? Math.round(marketPts - proj) : null;

      return {
        id: boardRowId(p.name, p.position, p.team),
        name: p.name,
        position: p.position,
        team: canonicalTeam(p.team) || 'FA',
        proj,
        expected,
        usageExpected,
        games: p.games ?? null,
        flatGames: flat,
        // Floor and ceiling arrive as multiples of the mean, not points: the
        // browser scores the stat line under this league's settings, so the
        // level is league-specific while the distribution's shape is not.
        floor: proj != null && p.floorRatio ? Math.round(proj * p.floorRatio) : null,
        ceiling: proj != null && p.ceilingRatio ? Math.round(proj * p.ceilingRatio) : null,
        spread:
          p.floorRatio && p.ceilingRatio
            ? Math.round((p.ceilingRatio - p.floorRatio) * 100) / 100
            : null,
        adp: p.adp ?? null,
        // How far real drafts scatter around that ADP — the input that makes a
        // survival estimate honest rather than a guess off our own ranking.
        adpStdev: p.adpStdev ?? null,
        adpRank: p.adpRank ?? null,
        ecrRank: p.ecrRank ?? null,
        marketProps: p.marketProps ?? null,
        marketAdjusted: Boolean(p.marketAdjusted),
        marketPts,
        marketEdge,
        bye: p.bye ?? null,
        confidence: p.confidence ?? null,
        // The model's own confidence-gated disagreement with the market. Zero
        // for most players, which is the honest answer and worth showing.
        modelEdge: p.edge ?? null,
        flags: p.flags ?? [],
        attribution: p.attribution ?? null,
        defaultRank: p.blendRank ?? (p.adpRank != null ? p.adpRank + 1000 : null),
        isPick: false,
      };
    })
    .sort((a, b) => (a.defaultRank ?? 9999) - (b.defaultRank ?? 9999));
};

export const assignModelTiers = (sortedRows, cliff = 0.14, floor = 8) => {
  let tier = 1;
  return sortedRows.map((row, i) => {
    if (i === 0) return { ...row, tier };
    const prev = sortedRows[i - 1];
    const a = prev.vor;
    const b = row.vor;
    if (a != null && b != null) {
      const drop = a - b;
      if (drop >= Math.max(Math.abs(a) * cliff, floor)) tier += 1;
    }
    return { ...row, tier };
  });
};
